import { OrganisationService } from '@sb-shared/services/organisation.service';
import { NgModel, UntypedFormGroup } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

@Component({
    selector: 'sb-currency-input',
    templateUrl: './currency-input.component.html',
    styleUrls: ['./currency-input.component.scss'],
    standalone: false
})
export class CurrencyInputComponent implements OnInit {

  @Input() isRequired: boolean;
  @Input() isDisabled: boolean;
  // When true, an unset value renders as an empty field instead of being coerced to "0.00".
  @Input() allowEmpty = false;
  @Input() maxDecimalPlaces: number;
  @Input() maxNumber: number;
  @Input() allowZero: boolean;
  @Input() symbolPrefix: string;
  @Input() id: string;
  // Accepts a number or a fixed-decimal string ("12.00"); stores back as the formatted string
  // after the user blurs (matches the legacy AngularJS sb-currency-input directive behaviour).
  @Input() model: any;
  @Output() modelChange: EventEmitter<any> = new EventEmitter();
  @Input() hidePrefix: boolean;
  // When supplied (e.g. by <sb-wizard>), the input renders as a reactive-form control via
  // formControlName=id; otherwise it uses [(ngModel)]="model" template-driven binding.
  @Input() form: UntypedFormGroup;
  @Input() step = 0.01;
  @Output() onBlur: EventEmitter<void> = new EventEmitter();
  // Template-driven inner control (non-reactive-form mode). Used to surface the field's validity
  // (pattern / required) to parent components, which can't otherwise see it — invalid text isn't
  // emitted through [model], so the parent's bound value alone can't tell a bad entry from a good one.
  @ViewChild('amountModel') private amountModel?: NgModel;
  min: number;
  lastValidValue: any;
  currencySymbol: string;
  currencyPattern = '';
  currencyExponent: number = (window as any).EveryBuddy?.Constants?.CurrencyExponent ?? 2;

  constructor(private organisation: OrganisationService) { }

  ngOnInit(): void {
    if (this.model == null || this.model === '') {
      this.model = this.allowEmpty ? '' : 0;
    }
    this.buildPattern();
    this.setMin();
    if (!(this.allowEmpty && this.model === '')) {
      // Match legacy: render initial value as fixed-decimal string so the field shows "0.00".
      this.formatModel();
    }
    this.lastValidValue = this.model;

    // Reactive-form mode: seed the FormControl from the [model] input if the caller pre-supplied
    // an initial value but the FormControl is still empty. Mirrors the legacy [ngModel]+[formControlName]
    // dual-binding pattern that callers like <sb-wizard> rely on.
    if (this.form && this.id) {
      const ctrl = this.form.get(this.id);
      if (ctrl && (ctrl.value == null || ctrl.value === '')) {
        ctrl.setValue(this.model, { emitEvent: false });
      }
    }

    this.organisation.getOrganisationCurrencySymbol().subscribe(symbol => {
      this.currencySymbol = symbol;
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.model) {
      // Mirror the legacy AngularJS sb-currency-input ($onChanges → formatNumber): whenever the
      // bound value is set programmatically (e.g. an amount prefilled from the outstanding balance),
      // reformat it to a fixed-decimal string so it renders "40.00" rather than "40". Display-only —
      // no emit — so the parent's value and the (change) data-flow are unaffected. Skipped on the
      // very first change (ngOnInit handles initial formatting) and for an intentionally empty field.
      if (!changes.model.firstChange && !(this.allowEmpty && (this.model == null || this.model === ''))) {
        this.formatModel();
      }
      this.lastValidValue = this.model;
    }
    if (changes.maxDecimalPlaces) {
      this.buildPattern();
    }
  }

  setMin() {
    this.min = this.allowZero ? 0 : this.step;
  }

  // True when the field's value passes its own validation (pattern + required). Lets parents disable
  // submit while the entry is invalid, since invalid text is intentionally not propagated via [model].
  get isValid(): boolean {
    const control = (this.form && this.id) ? this.form.get(this.id) : this.amountModel?.control;
    return control ? control.valid : true;
  }

  // Builds the validation regex for the input's [pattern] (ports the legacy sb-currency-input
  // directive). Without it the [pattern] binding was an empty string and validated nothing, so the
  // "not a valid amount" message never showed. Positive amounts only (the migrated component has no
  // allowNegative input), with up to `maxDecimalPlaces` decimals (defaults to the org currency exponent).
  private buildPattern(): void {
    if (this.maxDecimalPlaces == null) {
      this.maxDecimalPlaces = this.currencyExponent ?? 2;
    }
    const intPart = '[0-9]{0,14}';
    const decimalPart = this.maxDecimalPlaces > 0 ? `(\\.[0-9]{1,${this.maxDecimalPlaces}})?` : '';
    this.currencyPattern = `${intPart}${decimalPart}`;
  }

  // Reformats `this.model` in place to a fixed-decimal string. No emit — used during init only.
  private formatModel(): void {
    const dp = this.maxDecimalPlaces ?? 2;
    const num = parseFloat(String(this.model));
    if (!isNaN(num)) {
      this.model = num.toFixed(dp);
    }
  }

  change() {
    setTimeout(() => {
      // Pick the value source depending on which binding mode is active. In reactive-form mode
      // the FormControl is the source of truth; in ngModel mode, this.model is.
      const inFormMode = !!(this.form && this.id && this.form.get(this.id));
      const ctrl = inFormMode ? this.form.get(this.id) : null;

      const rawSource = inFormMode ? ctrl.value : this.model;
      const raw = rawSource == null || rawSource === '' ? '' : String(rawSource);

      const apply = (next: any) => {
        if (inFormMode) {
          ctrl.setValue(next);
        } else {
          this.model = next;
        }
        this.modelChange.emit(next);
      };

      if (raw === '') {
        apply(rawSource ?? '');
        return;
      }
      const num = parseFloat(raw);
      if (isNaN(num)) {
        // Keep the user's invalid text in the field so the [pattern] validation message stays
        // visible and the control remains invalid, instead of silently reverting to the last valid
        // value (which looked like the field just wiped the entry without explanation).
        return;
      }
      const dp = this.maxDecimalPlaces ?? 2;
      let value = parseFloat(num.toFixed(dp));
      if (value < this.min) {
        const last = parseFloat(String(this.lastValidValue ?? 0));
        value = isNaN(last) ? 0 : last;
      }
      if (typeof this.maxNumber === 'number' && !isNaN(this.maxNumber) && value > this.maxNumber) {
        value = this.maxNumber;
      }
      // Store as a fixed-decimal string ("12.00") so the input renders the trailing zeros,
      // mirroring the legacy AngularJS directive's `ngModel = ....toFixed(dp)` assignment.
      const formatted = value.toFixed(dp);
      this.lastValidValue = formatted;
      apply(formatted);
    })
  }

  blur() {
    this.onBlur.emit();
  }
}
